import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { CheckCircle, LogOut, Mail, Smartphone, Unlink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserQuery, useUpdateSettings, useGmailStatus } from '../lib/hooks';
import { api } from '../lib/api';

interface UserInfo {
  email?: string;
  usageQuota?: number;
  subscriptionTier?: string;
  deviceType?: string;
}

const DEVICE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'inreach', label: 'Garmin inReach' },
  { value: 'phone', label: 'Phone' },
  { value: 'zoleo', label: 'Zoleo' },
];

const inputClass =
  'block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-gray-500 focus:ring-gray-500';

const Settings: React.FC = () => {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { data: user } = useUserQuery() as { data?: UserInfo };
  const { data: gmailStatus, isLoading: gmailLoading } = useGmailStatus() as {
    data?: { connected: boolean };
    isLoading: boolean;
  };
  const { mutate: updateSettings } = useUpdateSettings();

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authUser) {
      navigate('/');
      return;
    }

    const gmailParam = searchParams.get('gmail');
    if (gmailParam === 'connected') {
      setMessage('Gmail connected successfully!');
      setTimeout(() => setMessage(''), 5000);
    } else if (gmailParam === 'error') {
      setError('Failed to connect Gmail. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, navigate, searchParams]);

  const gmailConnected = gmailStatus?.connected || false;

  const handleDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateSettings({ device_type: e.target.value });
  };

  const handleConnectGmail = async () => {
    try {
      const response = await api.get('/gmail/auth');
      window.location.href = response.data.authUrl;
    } catch (err) {
      console.error('Failed to get Gmail auth URL:', err);
      setError('Failed to start Gmail authorization. Please try again.');
    }
  };

  const handleDisconnectGmail = async () => {
    try {
      await api.post('/gmail/disconnect', {});

      queryClient.setQueryData('gmailStatus', { connected: false });
      queryClient.invalidateQueries('gmailStatus');
      setMessage('Gmail disconnected successfully.');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Failed to disconnect Gmail:', err);
      setError('Failed to disconnect Gmail. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Settings</h2>

      {message && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-green-700">{message}</p>
          <button type="button" onClick={() => setMessage('')} className="text-green-700 text-sm font-medium">
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button type="button" onClick={() => setError('')} className="text-red-700 text-sm font-medium">
            Dismiss
          </button>
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Account information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</p>
              <p className="mt-1 text-sm text-gray-900">{user?.email || ''}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscription tier
              </p>
              <p className="mt-1 text-sm text-gray-900">{user?.subscriptionTier || 'Free'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Quota</p>
              <p className="mt-1 text-sm text-gray-900">{user?.usageQuota ?? 0} remaining this month</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Smartphone className="w-4 h-4 mr-2 text-gray-400" />
            Device
          </h3>
          <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="device-select">
            Device
          </label>
          <select
            id="device-select"
            className={`${inputClass} w-64`}
            value={user?.deviceType || ''}
            onChange={handleDeviceChange}
          >
            <option value="" disabled>
              Select a device
            </option>
            {DEVICE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Connected services</h3>

          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div className="flex items-center">
              <Mail className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Gmail</p>
                <p className="text-sm text-gray-500">
                  {gmailConnected ? 'Email features enabled via SMS' : 'Connect to enable email commands via SMS'}
                </p>
              </div>
            </div>
            {!gmailLoading &&
              (gmailConnected ? (
                <button
                  type="button"
                  onClick={handleDisconnectGmail}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-red-600 text-sm font-medium rounded-md hover:bg-red-50"
                >
                  <Unlink className="w-4 h-4 mr-1" />
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleConnectGmail}
                  className="inline-flex items-center px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Connect
                </button>
              ))}
          </div>

          {gmailConnected && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <p className="text-sm font-medium text-gray-700 mb-2">Available SMS commands:</p>
              <ul className="text-sm text-gray-500 list-disc pl-5 space-y-1">
                <li>Text &quot;check my emails&quot; to see recent messages</li>
                <li>Text &quot;send email to [address] about [subject]&quot; to send emails</li>
                <li>Text &quot;search emails from [person]&quot; to find specific emails</li>
              </ul>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Log out</h3>
            <p className="text-sm text-gray-500">Sign out of this browser session.</p>
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
          >
            <LogOut className="w-4 h-4 mr-1" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
